/**
 * Essence orbs: a ball of spectral flame at rest, a ribbon when collected.
 *
 * A resting orb is drawn as a camera-facing flame (see essence-orb-shaders.ts)
 * hovering and bobbing over its drop point, with a soft pool of its color on
 * the floor beneath it, after the essence-orb cutscene reference sheet. The
 * ribbon underneath is hidden until the orb is collected.
 *
 * The ribbon is a length of ribbon coiled into a ring, twisting as it goes so
 * it alternates between face-on and edge-on, and slowly orbiting the drop
 * point. When the orb is collected the flame shrinks and fades as the coil
 * *unravels* out of it: the leading tip breaks away and tracks whoever
 * collected it while the rest of the ribbon pays out behind it, each point
 * along the length following at its own lag until the whole thing has been
 * drawn in.
 *
 * The ribbon draws with the shared `essenceStream` shader (see essence-stream-shaders.ts
 * for why that shader is registered in both the GLSL and WGSL stores - this
 * system runs inside the same module worker and would hit the same
 * `importScripts()` dead end). Textures are alpha masks, so the ribbon is
 * mostly transparent: what reads as the orb is the density of overlapping
 * wisps, not a solid surface.
 *
 * One instance owns the textures and one ribbon material per layer *per
 * color*. The flame and glow carry per-orb uniforms (phase, fade, a mix of
 * colors), so those get a material per orb - orbs are personal and few, and
 * the compiled shader itself is shared.
 */

import {
    Color3,
    Constants,
    Mesh,
    Texture,
    Vector3,
    VertexBuffer,
    VertexData,
    type Scene,
    type ShaderMaterial,
} from '@babylonjs/core';
import { easeInOutCubic } from '@sorrem/shared';
import { registerEssenceOrbShaders } from './essence-orb-shaders';
import { registerEssenceStreamShaders, type StreamProfile } from './essence-stream-shaders';
import { clamp01 } from './easing';
import { getSoulMistTexture } from './soul-mist';
import { createVfxShaderMaterial } from './vfx-shader-material';

const TAU = Math.PI * 2;

/** Samples along the ribbon's length. Two vertices each, so 40 is 80 vertices. */
const RIBBON_SAMPLES = 40;
/** How many times the ribbon wraps around its drop point while resting. */
const COIL_TURNS = 1.75;
/** Radius of the resting coil, in tiles. */
const COIL_RADIUS_TILES = 0.26;
/** Height of the coil's center above the floor, in tiles. */
export const ESSENCE_ORB_HOVER_HEIGHT_TILES = 0.45;
/** How far the ribbon rises and falls as it goes around the coil. */
const COIL_WEAVE_TILES = 0.09;
const COIL_WEAVE_CYCLES = 2;
/** Full twists of the ribbon's flat face along its length. */
const TWIST_TURNS = 1.25;
/** How fast a resting coil turns, in radians per second. */
const ORBIT_RADIANS_PER_SECOND = 1.9;
const BOB_AMPLITUDE_TILES = 0.06;
const BOB_RADIANS_PER_SECOND = 2.4;
/** Size of the flame quad, in tiles. The ball itself fills the lower ~60%. */
const FLAME_WIDTH_TILES = 0.6;
const FLAME_HEIGHT_TILES = 0.75;
/**
 * Where the flame ball's center sits up the quad (matches the shader's
 * `vec2(0.5, 0.4)`), so the quad can be offset to put that center on the
 * coil's center rather than the quad's middle.
 */
const FLAME_CENTER_V = 0.4;
/** Diameter of the pool of light on the floor, in tiles. */
const GLOW_DIAMETER_TILES = 0.95;
/** Lifts the glow off the floor just enough not to z-fight it. */
const GLOW_LIFT_TILES = 0.015;
const GLOW_INTENSITY = 0.55;
/** How much the glow brightens and dims with the bob, as a fraction of itself. */
const GLOW_PULSE = 0.18;
/**
 * The fraction of a pull over which the flame shrinks away. It is gone well
 * before the ribbon lands, so what reaches the collector is the ribbon alone.
 */
const FLAME_FADE_PULL = 0.45;
/** How small the flame shrinks to as it fades, as a fraction of its size. */
const FLAME_FADE_SCALE = 0.35;

/**
 * How much of the pull the tail lags the tip by. This is the unravel: at 0 the
 * whole ribbon moves as one rigid coil, and at 1 the tail only starts moving
 * as the tip arrives. The remaining `1 - TAIL_LAG` is how much of the pull any
 * one point has to cover its own distance in, so pushing this toward 1 makes
 * every point whip rather than glide.
 */
const TAIL_LAG = 0.50;

/**
 * The two ribbon layers: a wide soft veil with a brighter additive core
 * threaded through it. Reuses `StreamProfile` because it is exactly the
 * uniform set the shared `essenceStream` shader takes - but note `widthScale`
 * here is the ribbon's width in tiles at its widest point, not a multiplier on
 * a stream's width curve.
 */
const ORB_RIBBON_LAYERS: readonly StreamProfile[] = [
    {
        name: 'orb-veil',
        textureIndex: 2,
        widthScale: 1.2,
        scrollSpeed: 0.10,
        noiseStrength: 0.16,
        brightness: 0.95,
        baseOpacity: 0.32,
        vTiles: 3,
        wobbleAmplitude: 0.035,
        additive: false,
    },
    {
        name: 'orb-core',
        textureIndex: 0,
        widthScale: 0.6,
        scrollSpeed: 0.01,
        noiseStrength: 0.12,
        brightness: 1.6,
        baseOpacity: 0.55,
        vTiles: 5,
        wobbleAmplitude: 0.02,
        additive: true,
    },
];

/** A point in the scene, matching `ScenePosition` without importing it. */
interface Point3 {
    readonly x: number;
    readonly y: number;
    readonly z: number;
}

/** One orb to draw, as the render worker knows it. */
export interface EssenceOrbRenderState {
    readonly id: string;
    /** Scene-space floor position the coil is anchored above. */
    readonly floorPosition: Point3;
    /**
     * The orb's essence colors, dominant first. The flame and glow swirl up
     * to the first three together; the ribbon takes the dominant one.
     */
    readonly colorHexes: readonly string[];
    /**
     * Set while the orb is being drawn into whoever collected it: **linear**
     * 0..1 progress (this system applies the easing, because the unravel needs
     * to ease each point along the length separately) and the collector's
     * current scene-space floor position.
     */
    readonly pull: {
        readonly progress: number;
        readonly targetFloorPosition: Point3;
    } | null;
    /**
     * Set while something else carries the orb (the Veil's swirl, #259):
     * the flame is drawn centered on `center` (scene space), without its bob
     * and at `scale` of its size, with its floor glow on the floor under it.
     * Ignored while it is being pulled.
     */
    readonly held?: {
        readonly center: Point3;
        readonly scale: number;
    } | null;
}

/**
 * Per-sample constants shared by every orb: where along the coil this sample
 * sits, and how wide the ribbon is there. Independent of time, color and
 * position, so they are computed once for the process.
 */
interface RibbonSample {
    /** Angle around the coil at spin phase zero. */
    readonly angle: number;
    /** Vertical weave offset, in tiles. */
    readonly weaveY: number;
    /** Angle of the ribbon's flat face, for the twist. */
    readonly twist: number;
    /** Half the ribbon's width here, as a fraction of a layer's `widthScale`. */
    readonly halfWidthFraction: number;
    /** Normalized distance along the ribbon, 0 at the tip. */
    readonly s: number;
}

const RIBBON_SAMPLES_TABLE: readonly RibbonSample[] = Array.from({ length: RIBBON_SAMPLES }, (_, index) => {
    const s = index / (RIBBON_SAMPLES - 1);
    return {
        s,
        angle: -s * COIL_TURNS * TAU,
        weaveY: Math.sin(s * COIL_WEAVE_CYCLES * TAU) * COIL_WEAVE_TILES,
        twist: s * TWIST_TURNS * TAU,
        // Tapers to nothing at both ends, so neither end shows a cut edge - 
        // the shader only fades the ribbon across its width, not along it.
        halfWidthFraction: Math.pow(Math.sin(Math.PI * s), 0.75) / 2,
    };
});

/**
 * Scratch buffers for one ribbon's center line, reused across frames and orbs
 * so an unravelling orb allocates nothing per frame. Safe to share: a ribbon is
 * built start to finish inside a single synchronous call.
 */
const SCRATCH_POINTS: readonly Vector3[] = Array.from({ length: RIBBON_SAMPLES }, () => new Vector3());
const SCRATCH_PROGRESS = new Float64Array(RIBBON_SAMPLES);
const TANGENT = new Vector3();
const HORIZONTAL = new Vector3();
const SIDE = new Vector3();
const UP = new Vector3(0, 1, 0);
const TARGET_OFFSET = new Vector3();

interface OrbLayer {
    readonly mesh: Mesh;
    readonly profile: StreamProfile;
    /** Reused across frames so a pulling orb allocates nothing per frame. */
    readonly positions: Float32Array;
}

/** The resting orb's flame and the glow it casts. */
interface OrbBody {
    /** Billboarded; carries the bob and the fade-out scale. */
    readonly flame: Mesh;
    readonly flameMaterial: ShaderMaterial;
    readonly glow: Mesh;
    readonly glowMaterial: ShaderMaterial;
}

interface DrawnOrb {
    readonly layers: readonly OrbLayer[];
    readonly body: OrbBody;
    /** Offsets this orb's spin and bob so a cluster of drops never moves in lockstep. */
    readonly phase: number;
    /** Offsets the ribbon's twist and waver. Constant per orb, so the coil stays rigid under its orbit. */
    readonly twistPhase: number;
    /** The ribbon's color: the dominant one of `colorKey`. */
    colorHex: string;
    /** `colorHexes` joined, to notice a change without comparing arrays. */
    colorKey: string;
    floorPosition: Point3;
    pull: EssenceOrbRenderState['pull'];
    held: EssenceOrbRenderState['held'];
    /** True while the meshes hold baked resting geometry, spun rigidly about Y. */
    isResting: boolean;
}

export class EssenceOrbSystem {
    private readonly scene: Scene;
    private readonly textures: readonly [Texture, Texture, Texture];
    /** One material per layer per color, keyed `<layer name>:<color>`. */
    private readonly materials = new Map<string, ShaderMaterial>();
    private readonly orbs = new Map<string, DrawnOrb>();
    private timeSeconds = 0;

    public constructor(scene: Scene) {
        registerEssenceStreamShaders();
        registerEssenceOrbShaders();
        this.scene = scene;
        // Faded at the ribbon's edges: clamped across.
        this.textures = [getSoulMistTexture(scene, 1, true), getSoulMistTexture(scene, 2, true), getSoulMistTexture(scene, 3, true)];
    }

    /**
     * Reconciles the drawn orbs against the latest snapshot: adds ribbons for
     * new orbs, updates the ones still present, and disposes the rest.
     */
    public setOrbs(orbs: readonly EssenceOrbRenderState[]): void {
        for (const orb of orbs) {
            let ribbon = this.orbs.get(orb.id);
            if (!ribbon) {
                ribbon = this.createRibbon(orb);
                this.orbs.set(orb.id, ribbon);
            } else if (ribbon.colorKey !== orb.colorHexes.join(',')) {
                this.applyColors(ribbon, orb.colorHexes);
            }

            ribbon.floorPosition = orb.floorPosition;
            ribbon.pull = orb.pull;
            ribbon.held = orb.held;
        }

        // Nothing was removed: the loop above only ever adds, so equal sizes
        // here means the drawn set and the snapshot hold the same ids.
        if (this.orbs.size === orbs.length) {
            return;
        }

        const presentIds = new Set(orbs.map((orb) => orb.id));
        for (const [id, ribbon] of this.orbs) {
            if (!presentIds.has(id)) {
                this.disposeRibbon(ribbon);
                this.orbs.delete(id);
            }
        }
    }

    /** Advances the orbit, the bob, and any in-flight unravel. Call every frame. */
    public update(deltaSeconds: number): void {
        this.timeSeconds += deltaSeconds;

        // The shader scrolls its texture by `time`. Materials are shared per
        // layer and color, so this is a handful of writes a frame no matter how
        // many orbs are on screen.
        for (const material of this.materials.values()) {
            material.setFloat('time', this.timeSeconds);
        }

        for (const ribbon of this.orbs.values()) {
            if (ribbon.held && ribbon.pull === null) {
                // Carried: only the flame and its glow show, where it is carried to.
                for (const layer of ribbon.layers) {
                    layer.mesh.setEnabled(false);
                }
                this.updateHeldBody(ribbon, ribbon.held);
                continue;
            }
            const spin = (this.timeSeconds * ORBIT_RADIANS_PER_SECOND) + (ribbon.phase * TAU);
            // Linear, not eased: the easing is applied per point along the
            // ribbon inside writeGeometry, which is what makes the coil pay out
            // instead of sliding across as one piece.
            const pullLinear = ribbon.pull === null ? 0 : clamp01(ribbon.pull.progress);
            // The bob eases out as the ribbon leaves, or the tip would wobble
            // while it is trying to sit on the collector.
            const bob = Math.sin((this.timeSeconds * BOB_RADIANS_PER_SECOND) + (ribbon.phase * TAU))
                * BOB_AMPLITUDE_TILES
                * (1 - pullLinear);
            const centerY = ribbon.floorPosition.y + ESSENCE_ORB_HOVER_HEIGHT_TILES + bob;

            for (const layer of ribbon.layers) {
                layer.mesh.position.set(ribbon.floorPosition.x, centerY, ribbon.floorPosition.z);
                // Only the unravel shows the ribbon; at rest the flame is the orb.
                layer.mesh.setEnabled(ribbon.pull !== null);
            }
            this.updateBody(ribbon, centerY, bob, pullLinear);

            if (ribbon.pull === null) {
                // Resting: the coil's shape - twist included - is rigid under a
                // Y rotation, so the orbit is just the mesh's own rotation and
                // the vertices are left alone. Only a ribbon coming back from a
                // pull has to be rebuilt, and it is rebuilt at spin zero so the
                // rotation below is the one and only place the spin is applied.
                if (!ribbon.isResting) {
                    TARGET_OFFSET.setAll(0);
                    this.writeGeometry(ribbon, 0, 0, TARGET_OFFSET);
                    ribbon.isResting = true;
                }
                for (const layer of ribbon.layers) {
                    layer.mesh.rotation.y = spin;
                }
                continue;
            }

            // Unravelling: the shape changes every frame, so the spin is folded
            // into the vertex positions and the mesh's own rotation is cleared.
            const target = ribbon.pull.targetFloorPosition;
            TARGET_OFFSET.copyFromFloats(
                target.x - ribbon.floorPosition.x,
                (target.y + ESSENCE_ORB_HOVER_HEIGHT_TILES) - centerY,
                target.z - ribbon.floorPosition.z,
            );
            for (const layer of ribbon.layers) {
                layer.mesh.rotation.y = 0;
            }
            this.writeGeometry(ribbon, spin, pullLinear, TARGET_OFFSET);
            ribbon.isResting = false;
        }
    }

    public dispose(): void {
        for (const ribbon of this.orbs.values()) {
            this.disposeRibbon(ribbon);
        }
        this.orbs.clear();

        for (const material of this.materials.values()) {
            material.dispose();
        }
        this.materials.clear();
        // The mist textures are the scene's, shared with every other user.
    }

    /**
     * Places the flame over the coil's center and its glow on the floor, and
     * fades both out over the start of a pull. The glow breathes with the bob:
     * brighter as the orb dips toward the floor.
     */
    private updateBody(orb: DrawnOrb, centerY: number, bob: number, pullLinear: number): void {
        const { flame, flameMaterial, glow, glowMaterial } = orb.body;
        const fadeProgress = clamp01(pullLinear / FLAME_FADE_PULL);
        const fade = 1 - (fadeProgress * fadeProgress * (3 - (2 * fadeProgress)));
        const visible = fade > 0;

        flame.setEnabled(visible);
        glow.setEnabled(visible);
        if (!visible) {
            return;
        }

        const scale = FLAME_FADE_SCALE + ((1 - FLAME_FADE_SCALE) * fade);
        flame.position.set(orb.floorPosition.x, centerY, orb.floorPosition.z);
        flame.scaling.setAll(scale);
        flameMaterial.setFloat('time', this.timeSeconds);
        flameMaterial.setFloat('fade', fade);

        glow.position.set(orb.floorPosition.x, orb.floorPosition.y + GLOW_LIFT_TILES, orb.floorPosition.z);
        glow.scaling.setAll(scale);
        const pulse = 1 - ((bob / BOB_AMPLITUDE_TILES) * GLOW_PULSE);
        glowMaterial.setFloat('intensity', GLOW_INTENSITY * pulse * fade);
    }

    /** Places a carried orb's flame where it is carried, and its glow on the floor under it. */
    private updateHeldBody(orb: DrawnOrb, held: NonNullable<EssenceOrbRenderState['held']>): void {
        const { flame, flameMaterial, glow, glowMaterial } = orb.body;
        flame.setEnabled(true);
        glow.setEnabled(true);
        flame.position.set(held.center.x, held.center.y, held.center.z);
        flame.scaling.setAll(held.scale);
        flameMaterial.setFloat('time', this.timeSeconds);
        flameMaterial.setFloat('fade', 1);
        glow.position.set(held.center.x, orb.floorPosition.y + GLOW_LIFT_TILES, held.center.z);
        glow.scaling.setAll(held.scale);
        glowMaterial.setFloat('intensity', GLOW_INTENSITY * held.scale);
    }

    /**
     * Writes one ribbon's vertex positions for the current frame.
     *
     * `pullHead` is the eased progress of the leading tip; every other point
     * along the length derives its own progress from that, lagging by up to
     * `TAIL_LAG`. A point at progress 0 sits on the resting coil; at 1 it sits
     * on the collector. In between, its coil radius collapses as its center
     * travels, which is what makes the ribbon look like it is being drawn out
     * of a loop rather than dragged as a ring.
     */
    /**
     * Writes one ribbon's vertex positions for the current frame.
     *
     * `spin` is baked into the vertices only while unravelling - a resting coil
     * gets its orbit from the mesh's own rotation instead, so this is called
     * with zero there and the two modes agree at the moment of the switch.
     *
     * `pullLinear` is the pull's raw 0..1 progress. Each point takes a window
     * of that, starting later the further down the ribbon it sits, and eases
     * across its own window: a point at 0 sits on the resting coil, at 1 it sits
     * on the collector, and in between its coil radius collapses as its center
     * travels. That per-point window is the unravel, and the collapsing radius
     * is what makes it look drawn out of a loop rather than dragged as a ring.
     * Easing the whole ribbon first and lagging afterwards does not work - the
     * eased head is already almost home before the tail is allowed to move.
     */
    private writeGeometry(ribbon: DrawnOrb, spin: number, pullLinear: number, targetOffset: Vector3): void {
        for (let index = 0; index < RIBBON_SAMPLES; index += 1) {
            const sample = RIBBON_SAMPLES_TABLE[index]!;
            const pointLinear = pullLinear <= 0
                ? 0
                : clamp01((pullLinear - (sample.s * TAIL_LAG)) / (1 - TAIL_LAG));
            const progress = pointLinear <= 0 ? 0 : easeInOutCubic(pointLinear);
            const remaining = 1 - progress;
            const radius = COIL_RADIUS_TILES * remaining;
            // Subtracted, not added: a positive `Mesh.rotation.y` carries a point
            // from coil angle `a` to `a - spin` (Babylon is left-handed), and a
            // resting coil is spun that way instead of being rebuilt. Adding it
            // here would mirror the orbit the frame a pull starts.
            const angle = sample.angle - spin;

            SCRATCH_POINTS[index]!.copyFromFloats(
                (targetOffset.x * progress) + (Math.cos(angle) * radius),
                (targetOffset.y * progress) + (sample.weaveY * remaining),
                (targetOffset.z * progress) + (Math.sin(angle) * radius),
            );
            SCRATCH_PROGRESS[index] = progress;
        }

        for (const layer of ribbon.layers) {
            writeRibbonPositions(SCRATCH_POINTS, SCRATCH_PROGRESS, layer.profile, ribbon.twistPhase, layer.positions);
            layer.mesh.updateVerticesData(VertexBuffer.PositionKind, layer.positions);
            // An unravelling ribbon reaches all the way to the collector. Without
            // this it keeps the resting coil's bounding box and is culled the
            // moment the tip leaves it.
            layer.mesh.refreshBoundingInfo();
        }
    }

    private createRibbon(orb: EssenceOrbRenderState): DrawnOrb {
        const phase = hashToUnitInterval(orb.id);
        const twistPhase = hashToUnitInterval(`twist:${orb.id}`) * TAU;

        // The resting coil, at spin zero: the canonical shape every later frame
        // either rotates (resting) or overwrites (unravelling).
        for (let index = 0; index < RIBBON_SAMPLES; index += 1) {
            const sample = RIBBON_SAMPLES_TABLE[index]!;
            SCRATCH_POINTS[index]!.copyFromFloats(
                Math.cos(sample.angle) * COIL_RADIUS_TILES,
                sample.weaveY,
                Math.sin(sample.angle) * COIL_RADIUS_TILES,
            );
            SCRATCH_PROGRESS[index] = 0;
        }

        const layers = ORB_RIBBON_LAYERS.map((profile, index) => {
            const mesh = new Mesh(`essence-orb-${orb.id}-${profile.name}-${index}`, this.scene);
            const positions = new Float32Array(RIBBON_SAMPLES * 2 * 3);
            writeRibbonPositions(SCRATCH_POINTS, SCRATCH_PROGRESS, profile, twistPhase, positions);

            const vertexData = new VertexData();
            vertexData.positions = Array.from(positions);
            vertexData.uvs = buildRibbonUvs(profile.vTiles);
            vertexData.indices = buildRibbonIndices();
            // Updatable: an unravelling ribbon rewrites its positions in place.
            vertexData.applyToMesh(mesh, true);

            mesh.isPickable = false;
            mesh.material = this.acquireMaterial(profile, resolveDominantColor(orb.colorHexes));

            return { mesh, profile, positions };
        });

        const body = this.createBody(orb, phase);

        return {
            layers,
            body,
            phase,
            twistPhase,
            colorHex: resolveDominantColor(orb.colorHexes),
            colorKey: orb.colorHexes.join(','),
            floorPosition: orb.floorPosition,
            pull: orb.pull,
            held: orb.held,
            isResting: true,
        };
    }

    /**
     * The flame quad, offset so the ball's center (not the quad's middle) sits
     * on the mesh origin, and the floor glow quad lying flat.
     */
    private createBody(orb: EssenceOrbRenderState, phase: number): OrbBody {
        const flame = new Mesh(`essence-orb-flame-${orb.id}`, this.scene);
        const bottom = -FLAME_HEIGHT_TILES * FLAME_CENTER_V;
        const top = FLAME_HEIGHT_TILES * (1 - FLAME_CENTER_V);
        const halfWidth = FLAME_WIDTH_TILES / 2;
        buildQuad([
            -halfWidth, bottom, 0,
            halfWidth, bottom, 0,
            -halfWidth, top, 0,
            halfWidth, top, 0,
        ]).applyToMesh(flame);
        flame.billboardMode = Mesh.BILLBOARDMODE_ALL;
        flame.isPickable = false;

        const flameMaterial = this.createBodyMaterial(`essence-orb-flame-${orb.id}`, 'essenceOrbFlame', ['time', 'phase', 'fade']);
        flameMaterial.setFloat('time', this.timeSeconds);
        flameMaterial.setFloat('phase', phase);
        flameMaterial.setFloat('fade', 1);
        flame.material = flameMaterial;

        const glow = new Mesh(`essence-orb-glow-${orb.id}`, this.scene);
        const radius = GLOW_DIAMETER_TILES / 2;
        buildQuad([
            -radius, 0, -radius,
            radius, 0, -radius,
            -radius, 0, radius,
            radius, 0, radius,
        ]).applyToMesh(glow);
        glow.isPickable = false;

        const glowMaterial = this.createBodyMaterial(`essence-orb-glow-${orb.id}`, 'essenceOrbGlow', ['intensity']);
        glowMaterial.setFloat('intensity', GLOW_INTENSITY);
        glowMaterial.alphaMode = Constants.ALPHA_ADD;
        glow.material = glowMaterial;

        const body: OrbBody = { flame, flameMaterial, glow, glowMaterial };
        setBodyColors(body, orb.colorHexes);
        return body;
    }

    private createBodyMaterial(name: string, shaderName: string, uniforms: readonly string[]): ShaderMaterial {
        return createVfxShaderMaterial(this.scene, name, shaderName, { uniforms: ['worldViewProjection', ...uniforms, 'colorA', 'colorB', 'colorC'] });
    }

    private applyColors(orb: DrawnOrb, colorHexes: readonly string[]): void {
        const colorHex = resolveDominantColor(colorHexes);
        for (const layer of orb.layers) {
            layer.mesh.material = this.acquireMaterial(layer.profile, colorHex);
        }
        setBodyColors(orb.body, colorHexes);
        orb.colorHex = colorHex;
        orb.colorKey = colorHexes.join(',');
    }

    private disposeRibbon(orb: DrawnOrb): void {
        for (const layer of orb.layers) {
            // Ribbon materials are shared per layer/color and owned by this
            // system - only the geometry belongs to the orb.
            layer.mesh.dispose();
        }
        // The flame and glow materials are the orb's own.
        orb.body.flame.dispose();
        orb.body.flameMaterial.dispose();
        orb.body.glow.dispose();
        orb.body.glowMaterial.dispose();
    }

    /**
     * One material per layer per color. `shaderLanguage` must be explicit:
     * Babylon never infers WGSL from the active engine, even where a
     * `ShadersStoreWGSL` entry exists - see essence-stream-shaders.ts.
     */
    private acquireMaterial(profile: StreamProfile, colorHex: string): ShaderMaterial {
        const key = `${profile.name}:${colorHex}`;
        const existing = this.materials.get(key);
        if (existing) {
            return existing;
        }

        const material = createVfxShaderMaterial(this.scene, `essence-orb-${key}`, 'essenceStream', {
            uniforms: ['worldViewProjection', 'time', 'scrollSpeed', 'noiseStrength', 'streamColor', 'brightness', 'opacity'],
            samplers: ['streamTexture'],
        });
        material.setTexture('streamTexture', this.textures[profile.textureIndex]);
        material.setFloat('scrollSpeed', profile.scrollSpeed);
        material.setFloat('noiseStrength', profile.noiseStrength);
        material.setFloat('brightness', profile.brightness);
        material.setFloat('opacity', profile.baseOpacity);
        material.setColor3('streamColor', hexToColor3(colorHex));
        material.setFloat('time', this.timeSeconds);
        if (profile.additive) {
            material.alphaMode = Constants.ALPHA_ADD;
        }

        this.materials.set(key, material);
        return material;
    }

}

/**
 * Expands a ribbon center line into the two edge vertices per sample.
 *
 * The ribbon's flat face twists along its length, so it alternates between
 * face-on and edge-on the way a real ribbon does. The twist depends only on the
 * sample and the orb's own phase - never on the orbit - which is what keeps a
 * resting coil rigid under a Y rotation and lets its geometry be baked once.
 * As a point is drawn in, its twist relaxes toward flat-on to the direction of
 * travel, so the tip reads as a streak rather than a spinning blade.
 */
function writeRibbonPositions(
    points: readonly Vector3[],
    localProgress: Float64Array,
    profile: StreamProfile,
    twistPhase: number,
    out: Float32Array,
): void {
    for (let index = 0; index < points.length; index += 1) {
        const sample = RIBBON_SAMPLES_TABLE[index]!;
        const point = points[index]!;
        const previous = points[Math.max(0, index - 1)]!;
        const next = points[Math.min(points.length - 1, index + 1)]!;

        next.subtractToRef(previous, TANGENT);
        if (TANGENT.lengthSquared() < 1e-8) {
            TANGENT.copyFromFloats(0, 0, 1);
        }
        TANGENT.normalize();

        // Perpendicular to travel and level with the floor. Falls back to a
        // fixed axis where travel is straight up or down and the cross product
        // collapses.
        Vector3.CrossToRef(TANGENT, UP, HORIZONTAL);
        if (HORIZONTAL.lengthSquared() < 1e-8) {
            HORIZONTAL.copyFromFloats(1, 0, 0);
        }
        HORIZONTAL.normalize();

        // HORIZONTAL and UP are perpendicular unit vectors, so this stays unit
        // length without a normalize.
        const twist = (sample.twist + twistPhase) * (1 - (localProgress[index] ?? 0));
        const cos = Math.cos(twist);
        const sin = Math.sin(twist);
        SIDE.copyFromFloats(
            HORIZONTAL.x * cos,
            (HORIZONTAL.y * cos) + sin,
            HORIZONTAL.z * cos,
        );

        const halfWidth = sample.halfWidthFraction * profile.widthScale;
        // A gentle lateral waver so the two layers never sit exactly on top of
        // each other and the ribbon does not read as a clean extrusion. Keyed
        // off the sample only, so it too survives the rigid-rotation shortcut.
        const waver = Math.sin((sample.s * Math.PI * 4) + twistPhase) * profile.wobbleAmplitude;
        const centerX = point.x + (SIDE.x * waver);
        const centerY = point.y + (SIDE.y * waver);
        const centerZ = point.z + (SIDE.z * waver);

        const offset = index * 6;
        out[offset] = centerX - (SIDE.x * halfWidth);
        out[offset + 1] = centerY - (SIDE.y * halfWidth);
        out[offset + 2] = centerZ - (SIDE.z * halfWidth);
        out[offset + 3] = centerX + (SIDE.x * halfWidth);
        out[offset + 4] = centerY + (SIDE.y * halfWidth);
        out[offset + 5] = centerZ + (SIDE.z * halfWidth);
    }
}

/** U across the ribbon's width, V along its length - matches the shader's expectations. */
function buildRibbonUvs(vTiles: number): number[] {
    const uvs: number[] = [];
    for (const sample of RIBBON_SAMPLES_TABLE) {
        uvs.push(0, sample.s * vTiles, 1, sample.s * vTiles);
    }
    return uvs;
}

function buildRibbonIndices(): number[] {
    const indices: number[] = [];
    for (let index = 0; index < RIBBON_SAMPLES - 1; index += 1) {
        const a = index * 2;
        const b = a + 1;
        const c = a + 2;
        const d = a + 3;
        indices.push(a, c, b, b, c, d);
    }
    return indices;
}

/** A two-triangle quad from four corners: bottom-left, bottom-right, top-left, top-right. */
function buildQuad(positions: readonly number[]): VertexData {
    const vertexData = new VertexData();
    vertexData.positions = [...positions];
    vertexData.uvs = [0, 0, 1, 0, 0, 1, 1, 1];
    vertexData.indices = [0, 2, 1, 1, 2, 3];
    return vertexData;
}

/** The ribbon's color: the dominant one, falling back to white for an empty list. */
function resolveDominantColor(colorHexes: readonly string[]): string {
    return colorHexes[0] ?? '#ffffff';
}

/**
 * Feeds the flame and glow their three color slots. An orb with fewer than
 * three colors repeats them, so a single-color orb swirls one solid hue and
 * a two-color orb weights the dominant one.
 */
function setBodyColors(body: OrbBody, colorHexes: readonly string[]): void {
    const first = resolveDominantColor(colorHexes);
    const second = colorHexes[1] ?? first;
    const third = colorHexes[2] ?? first;
    for (const material of [body.flameMaterial, body.glowMaterial]) {
        material.setColor3('colorA', hexToColor3(first));
        material.setColor3('colorB', hexToColor3(second));
        material.setColor3('colorC', hexToColor3(third));
    }
}

/** Stable 0..1 from an orb id, for per-orb phase offsets. */
function hashToUnitInterval(value: string): number {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return ((hash >>> 0) % 10000) / 10000;
}

function hexToColor3(hex: string): Color3 {
    const normalized = hex.replace('#', '');
    return new Color3(
        Number.parseInt(normalized.slice(0, 2), 16) / 255,
        Number.parseInt(normalized.slice(2, 4), 16) / 255,
        Number.parseInt(normalized.slice(4, 6), 16) / 255,
    );
}
